import _ from "lodash"
import { ConcurrentTaskRunner, WorkItemType, getConceptStatusValue, SyncItemStatus, WorkItemKey, proposeApptExport, getDisplayIdentity, SyncResultStatus, SyncStrategy, SyncDeviceToCloudOrdinalsStatus, sleep } from "@fusion-advantage/formlib"
import { AppDispatch, AppThunk } from "../appStore"
import { selectFusionApiAxios, selectPlainAxios } from "../selectAxios"
import { incrementSyncDenominatorEstimate, selectSyncResult, setSyncItemSubtaskTick, setSyncEnd, setSyncItemResult, setSyncStart, setSyncCanCancel, setSyncNotice, SyncSliceState, setSyncPendingDeviceToCloudOrdinals, dropSyncPendingDeviceToCloudOrdinal } from "../syncSlice"
import {
	cloudToDeviceSyncAppStatus,
	cloudToDeviceSyncAppts,
	cloudToDeviceSyncBranch,
	cloudToDeviceSyncEncounterFile,
	cloudToDeviceSyncEtagTest,
	cloudToDeviceSyncExport,
	cloudToDeviceSyncLogout,
	cloudToDeviceSyncOfflineTable,
	cloudToDeviceSyncRenderer,
	cloudToDeviceSyncTodos,
} from "./cloudToDeviceSyncItem"
import { ApptEncounter, replaceApptEncounters } from "../../dal/ldbApptEncounter"
import { ApptEncounterGuidSelector } from "../../dal2/ApptEncounterGuidSelector"
import { AxiosInstance } from "axios"
import { OfflineTableId } from "../../dal/OfflineTable"
import { pushLogEntry } from "../logSlice"
import { appStatusBodyToUserIdentities, CloudToDeviceAppStatusOk, CloudToDeviceExportArgs } from "./ZCloudToDevice"
import { DeviceToCloudSyncInfo } from "./ZDeviceToCloud"
import { appError } from "../errorSlice"
import { err, ok, Result } from "neverthrow"
import { AppModal, dismissAppModal, setLdng, unshiftAppModal } from "../appSlice"
import { SyncResult } from "./SyncResult"
import { DeviceToCloudBlobSyncItem, selectDeviceToCloudBlobSyncItems } from "../../dal/ldbDeviceToCloudBlob"
import { SyncItemDeferredWriteResult, SyncItemDeferredWriteTask, SyncItemKey, SyncItemResult, syncItemResultToStateResult, SyncItemSubtaskType, SyncItemType } from "./SyncItemResult"
import { deviceToCloudSyncEncounterFile, deviceToCloudSyncItem } from "./deviceToCloudSyncItem"
import { SyncConfig, syncShouldDefer, syncShouldDownloadOfflineTables, syncShouldDownloadWorkItems, syncShouldInstallImmediateBundleUpdates, syncShouldShowModalDelayed, syncShouldShowModalImmediate, syncShouldSuppressErrorAlert, SyncType } from "./SyncConfig"
import { selectSessionIsExpired } from "../selectSession"
import { selectContentIsWritableForm } from "../selectContent"
import { getBuildNumber, getIosVersion, getJsBundle } from "../../lib/appInfo"
import { XFrontendRequired } from "../../lib/XFrontend"
import { BundleInfo, CapacitorUpdater } from "@capgo/capacitor-updater"
import { App as CapacitorApp } from '@capacitor/app'
import { CodeNext, ConceptValueDataType, Export, FlowmapParser, FusionError, proposeUseOriginalRendererForDocgen, serializeFusionError } from "@fusion-advantage/backcompat"
import { safeDecode } from "@fusion-advantage/formrender"
import { rendererInfoFromFlowmapParser } from "../../lib/flowmapBranchInfo"

export const appSync = (cfg: SyncConfig): AppThunk<Promise<SyncResult>> => async (dispatch, getState) => {
	// in branch preview every sync (e.g. a Sync modal retry) is the preview
	// sync, so nothing ever downloads the user's work items into it
	const preview = getState().app.preview
	if(preview) {
		switch(cfg.type) {
			case SyncType.Preview:
			case SyncType.FrontendRequired:
			case SyncType.Logout:
				break
			default:
				dispatch(pushLogEntry(`[appSync] ${cfg.type} sync replaced by ${SyncType.Preview} in branch preview`))
				cfg = {
					type: SyncType.Preview,
					adhocBranchGuid: preview.branchGuid,
				}
		}
	}
	if(getState().sync.startedAt && !getState().sync.endedAt) {
		if(!import.meta.env.VITEST) {
			dispatch(appError({
				err: serializeFusionError('appSync', 'sync() requested but already in progress'),
				suppressAlert: syncShouldSuppressErrorAlert(cfg.type),
			}))
		}
		return err({ status: SyncResultStatus.Pending })
	}
	if(syncShouldDefer(cfg.type, getState().app.syncStrategy)) {
		dispatch(pushLogEntry(`[appSync] sync dispatched but deferred due to strategy`))
		return ok()
	}
	dispatch(setSyncStart(cfg))
	dispatch(pushLogEntry(`[appSync] started at ${getState().sync.startedAt}`))
	if(syncShouldShowModalImmediate(cfg.type)) {
		dispatch(unshiftAppModal(AppModal.Sync))
	}
	try {
		const runner = new SyncRunner(
			dispatch as AppDispatch,
			getState().app.clientId,
			getState().app.syncStrategy,
			() => getState().sync,
			selectFusionApiAxios(getState()),
			selectPlainAxios(getState()),
			selectSessionIsExpired(getState()),
			() => selectContentIsWritableForm(getState()),
			cfg,
		)
		await runner.run()
	} catch(ex) {
		// last resort
		dispatch(appError({
			err: serializeFusionError('syncThunk', ex),
			suppressAlert: syncShouldSuppressErrorAlert(cfg.type),
		}))
		// do not stop
	}
	dispatch(setSyncEnd())
	const elapsedMs = Date.now() - (getState().sync.startedAt ?? 0)
	dispatch(pushLogEntry(`[sync] ended (+${elapsedMs}ms)`))
	const rSync = selectSyncResult(getState())
	if(rSync.isErr()) {
		dispatch(pushLogEntry(`[sync] result Error ${rSync.error.status}`))
		// TODO lets make sure this goes to the general error log
		// if it's really weird
		dispatch(pushLogEntry(`[sync] state dump: ${JSON.stringify(getState().sync)}`))
	} else {
		dispatch(pushLogEntry(`[sync] result Ok`))
		dispatch(dismissAppModal(AppModal.Sync))
	}
	return rSync
}

const _setItemResult = (key: SyncItemKey, r: SyncItemResult<unknown>): AppThunk<Promise<void>> => async (dispatch) => {
	let status: SyncItemStatus
	let error: FusionError|null = null
	if(r.isErr()) {
		if(r.error.error) {
			error = r.error.error
		}
		status = r.error.status
	} else {
		status = r.value.status
	}
	dispatch(pushLogEntry(`[SyncRunner] ${JSON.stringify(key)} -> ${status}`))
	if(error && !import.meta.env.VITEST) {
		dispatch(appError({
			err: error,
			suppressAlert: true,
		}))
	}
	const stateResult = syncItemResultToStateResult(r)
	dispatch(setSyncItemResult({ key, stateResult }))
}

const _subtaskTick = (key: SyncItemKey, subtaskType: SyncItemSubtaskType): AppThunk<Promise<void>> => async (dispatch, getState) => {
	dispatch(setSyncItemSubtaskTick({key, subtaskType}))
	// NOTE
	// the below code would log out every tick for performance debugging
	// but now that everything is pretty snappy, it just creates noise
	// const item = selectSyncItem(getState().sync, key)
	// const task1 = item?.subtasks.at(-1)
	// const task2 = item?.subtasks.at(-2)
	// const elapsedMs = (task1 && task2) ? (task1.endedAt - task2.endedAt) : 0
	// dispatch(pushLogEntry(`[SyncRunner] tick ${JSON.stringify(key)} ${task1?.type} +${elapsedMs}ms`))
}

export type SubtaskTicker = (subtaskType: SyncItemSubtaskType) => void

interface KeyedDeferredWriteTask {
	key: SyncItemKey
	task: SyncItemDeferredWriteTask
}

export class SyncRunner {
	static readonly name = 'SyncRunner'
	static readonly maxConcurrentTasks = 3
	runner = new ConcurrentTaskRunner(SyncRunner.maxConcurrentTasks)
	batchedUploads: DeviceToCloudBlobSyncItem[][] = []
	uploadsSuccess: boolean = true
	deviceToCloudCodeNext = new Map<string, CodeNext>()
	encounterExports: Export[] = []
	rendererNumbers = new Map<number, boolean>()
	branchGuids = new Map<string, boolean>()
	branchFileCids = new Map<string, string[]>()
	apptEncounters: ApptEncounter[] = []
	deferredWriteTasks: KeyedDeferredWriteTask[] = []
	dispatch: AppDispatch
	clientId: string
	syncStrategy: SyncStrategy
	getSyncState: () => SyncSliceState
	axInst: AxiosInstance
	plainAxInst: AxiosInstance
	writableFormIsOpen: () => boolean
	sessionIsExpired: boolean
	cfg: SyncConfig

	constructor(
		dispatch: AppDispatch,
		clientId: string,
		syncStrategy: SyncStrategy,
		getSyncState: () => SyncSliceState,
		axInst: AxiosInstance,
		plainAxInst: AxiosInstance,
		sessionIsExpired: boolean,
		writableFormIsOpen: () => boolean,
		cfg: SyncConfig
	) {
		this.dispatch = dispatch
		this.clientId = clientId
		this.syncStrategy = syncStrategy
		this.getSyncState = getSyncState
		this.axInst = axInst
		this.plainAxInst = plainAxInst
		this.sessionIsExpired = sessionIsExpired
		this.writableFormIsOpen = writableFormIsOpen
		this.cfg = cfg
		this.runner.name = SyncRunner.name
	}

	setItemResult(key: SyncItemKey, r: SyncItemResult<unknown>) {
		this.dispatch(_setItemResult(key, r))
	}

	subtaskTick(key: SyncItemKey, subtaskType: SyncItemSubtaskType) {
		this.dispatch(_subtaskTick(key, subtaskType))
	}

	startTask(key: SyncItemKey): SubtaskTicker {
		this.setItemResult(key, err({ status: SyncItemStatus.Pending }))
		this.subtaskTick(key, SyncItemSubtaskType.Start)
		return (subtaskType) => {
			this.subtaskTick(key, subtaskType)
		}
	}

	pushDeferredWriteTask(key: SyncItemKey, r: SyncItemDeferredWriteResult<unknown>) {
		if(r.isOk() && r.value.deferredWriteTask) {
			this.deferredWriteTasks.push({
				key,
				task: r.value.deferredWriteTask,
			})
		}
	}

	endTask(key: SyncItemKey, r: SyncItemResult<unknown>) {
		this.setItemResult(key, r)
		this.subtaskTick(key, SyncItemSubtaskType.End)
	}

	// if a writable form is open on the device, we don't want to
	// download and write potentially updated content underneath it.
	// instead it's safest to download new content only when there's
	// no form also actively writing drafts to the database.
	// because forms can be opened during a long background sync,
	// which can be especially protracted on slow cell connections,
	// we allow the tasks to check themselves for self-canceling
	// upon start.
	cancelTask(key: SyncItemKey) {
		this.setItemResult(key, err({status: SyncItemStatus.Canceled}))
		this.subtaskTick(key, SyncItemSubtaskType.End)
	}

	async run() {
		if(this.cfg.type === SyncType.FrontendRequired) {
			this.dispatch(unshiftAppModal(AppModal.Sync))
			const updateTask = this.createFrontendUpdateTask(this.cfg.frontendRequired)
			if(updateTask) {
				this.runner.push(updateTask)
				await this.runner.finished()
				return
			}
		}
		this.dispatch(setSyncCanCancel(true))
		this.dispatch(incrementSyncDenominatorEstimate(1))
		let appStatusResult: SyncItemResult<CloudToDeviceAppStatusOk>
		{
			const key: SyncItemKey = {
				type: SyncItemType.AppStatus,
			}
			const ticker = this.startTask(key)
			// if we already know the session is expired,
			// don't try to call out to appStatus,
			// just get the user pushed back to the login screen
			// so they can reauthenticate
			if(this.sessionIsExpired) {
				appStatusResult = err({
					status: SyncItemStatus.NoAuth
				})
			} else if(this.cfg.type === SyncType.Logout) {
				appStatusResult = await cloudToDeviceSyncLogout()
			} else {
				appStatusResult = await cloudToDeviceSyncAppStatus(ticker, this.axInst)
			}
			this.endTask(key, appStatusResult)
			// without connection, valid parse of appStatus, or current auth,
			// there are no downloads and uploads to perform
			if(!appStatusResult.isOk() || (this.cfg.type === SyncType.Logout)) {
				return
			}
		}
		// we've waited to display the modal to avoid a flash on screen
		// if there's simply no connection or no auth
		if(syncShouldShowModalDelayed(this.cfg.type)) {
			this.dispatch(unshiftAppModal(AppModal.Sync))
		}
		if(appStatusResult.value.value.FrontendRequired) {
			const updateTask = this.createFrontendUpdateTask(appStatusResult.value.value.FrontendRequired)
			if(updateTask) {
				// perform update and exit sync
				this.runner.push(updateTask)
				await this.runner.finished()
				return
			}
		}
		const identities = appStatusBodyToUserIdentities(appStatusResult.value.value)
		const upn = getDisplayIdentity(identities)?.upn
		if(!upn) { 
			this.setItemResult({type: SyncItemType.AppStatus}, err({
				status: SyncItemStatus.Unknown,
				error: serializeFusionError(SyncRunner.name, 'failed assertion AuthedIdentity !UPN')
			}))
			return
		}
		this.runner.push(this.createBatchDeviceToCloudTask())
		await this.runner.finished()
		await this.pushBatchedUploadTasks()
		if(syncShouldDownloadOfflineTables(this.cfg.type)) {
			this.runner.push(this.createEtagTestTask(appStatusResult.value.value.EtagTestHash))
			this.runner.push(this.createOfflineTableTask(OfflineTableId.DrugProduct))
			this.runner.push(this.createOfflineTableTask(OfflineTableId.DrugProductMap))
			this.runner.push(this.createOfflineTableTask(OfflineTableId.DrugSearchWord))
			this.runner.push(this.createOfflineTableTask(OfflineTableId.DrugIcd))
			this.runner.push(this.createOfflineTableTask(OfflineTableId.MaterialInventory))
			this.runner.push(this.createOfflineTableTask(OfflineTableId.DashboardNotification))
		}
		// if any uploads have failed, finish downloading offline tables
		// but do not proceed with other downloads
		if(!this.uploadsSuccess) {
			await this.runner.finished()
			return
		}
		if(this.cfg.adhocEncounterGuid) {
			this.pushTodoExportTask(this.cfg.adhocEncounterGuid, upn)
		}
		if(this.cfg.adhocBranchGuid) {
			this.runner.push(this.createBranchTask(this.cfg.adhocBranchGuid, true))
		}
		this.runner.push(this.createCloudToDeviceLdngTask(true))
		if(syncShouldDownloadWorkItems(this.cfg.type)) {
			this.runner.push(this.createTodosTask(upn))
			this.runner.push(this.createApptsTask(upn))
			// pause until every task and all derived tasks have completed
			// so we have a full list of apptEncounters and encounters
			await this.runner.finished()
			this.runner.push(this.createReplaceApptEncountersTask(upn))
			this.runner.push(this.createEncounterFileTasks())
		}
		await this.runner.finished()
		this.dispatch(setSyncCanCancel(false))
		this.runner.push(this.createRunDeferredWritesTask())
		await this.runner.finished()
		this.runner.push(this.createCloudToDeviceLdngTask(false))
		await this.runner.finished()
		// TODO think through this and re-enable with comments
		// await cleanupLocalDatabase()
	}

	createFrontendUpdateTask(required: XFrontendRequired) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.FrontendUpdate,
		}
		// only prompt user to download new build
		// when first logging in
		// (this is not a message users should ever see
		// if mdm is working correctly)
		if(this.cfg.type === SyncType.FrontendRequired && required.UpdateMobileBuild) {
			return async () => {
				this.endTask(key, err({
					status: SyncItemStatus.UpdateRequired,
				}))
				this.dispatch(setSyncNotice(`An app update is required; please contact support for assistance.  (Current build: ${getBuildNumber()})`))
			}
		}
		// kick off new bundle download if urgent
		// or when first logging in
		if(
			required.UpdateJsBundle &&
			(
				(required.UpdateJsBundleImmediate && syncShouldInstallImmediateBundleUpdates(this.cfg.type)) ||
				this.cfg.type === SyncType.FrontendRequired
			)
		) {
			return async () => {
				this.startTask(key)
				const r = await (async (): Promise<Result<void, FusionError>> => {
					// NOTE now using long-form static.fusion-advantage.com domain here
					// because the static.fadv.link shortcut domain was getting flagged as
					// suspicious by security software
					let bundleInfo: BundleInfo
					try {
						bundleInfo = await CapacitorUpdater.download({
							url: `https://static.fusion-advantage.com/app-jsbundle/jsbundle-${required.MinJsBundle}.zip`,
							version: `0.0.${required.MinJsBundle}`,  // semver required
						})
					} catch(ex) {
						return err(serializeFusionError('createFrontendUpdateTask', ex))
					}
					if(!bundleInfo) {
						return err(serializeFusionError('createFrontendUpdateTask', 'expected bundleInfo but null'))
					}
					const { isActive } = await CapacitorApp.getState()
					if(!isActive) {
						return err(serializeFusionError('createFrontendUpdateTask', 'update failed because app is not active'))
					}
					try {
						await CapacitorUpdater.set(bundleInfo)
					} catch(ex) {
						return err(serializeFusionError('createFrontendUpdateTask', ex))
					}
					return ok()
				})()
				if(r.isErr()) {
					this.endTask(key, err({
						status: SyncItemStatus.Unknown,
						error: r.error,
					}))
				} else {
					this.endTask(key, ok({
						status: SyncItemStatus.Ok,
						value: null,
					}))
				}
			}
		}
		return null
	}

	createBatchDeviceToCloudTask() {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.BatchDeviceToCloud,
		}
		return async () => {
			const ticker = this.startTask(key)
			this.dispatch(setSyncPendingDeviceToCloudOrdinals(SyncDeviceToCloudOrdinalsStatus.Querying))
			const rSelect = await selectDeviceToCloudBlobSyncItems()
			ticker(SyncItemSubtaskType.LdbRead1)
			if(rSelect.isErr()) {
				this.endTask(
					key,
					err({
						status: SyncItemStatus.Unknown,
						error: rSelect.error,
					})
				)
				return
			}
			this.dispatch(setSyncPendingDeviceToCloudOrdinals(rSelect.value.ordinals))
			this.batchedUploads = rSelect.value.batches
			this.endTask(
				key,
				ok({
					status: SyncItemStatus.Ok,
					value: null,
				})
			)
		}
	}

	async pushBatchedUploadTasks() {
		let hasTasks = false
		for(let batch of this.batchedUploads) {
			for(let {itemKey, ordinal, additionalPendingForEncounter} of batch) {
				hasTasks = true
				switch(itemKey.type) {
					case SyncItemType.DeviceToCloudFile:
						this.runner.push(this.createDeviceToCloudFileTask(itemKey, ordinal))
						break
					case SyncItemType.EncounterStatus:
					case SyncItemType.Import:
						this.runner.push(this.createDeviceToCloudItemTask(itemKey, ordinal, additionalPendingForEncounter))
						break
				}
			}
			// ensure all items in batch completed successfully
			// before continuing to next batch
			await this.runner.finished()
			if(!this.uploadsSuccess) {
				// error on current batch;
				// do not proceed with next batch
				break
			}
		}
		// if there were upload tasks, pause for 1s to allow dan's database
		// to process uploaded encounter changes before requesting
		// replacement versions of them as appts and todos
		if(hasTasks) {
			this.runner.push(this.createImportProcessingAllowanceTask())
			await this.runner.finished()
		}
	}

	createDeviceToCloudFileTask(key: SyncItemKey, ordinal: number) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		return async () => {
			const ticker = this.startTask(key)
			const result = await deviceToCloudSyncEncounterFile(ticker, this.axInst, this.plainAxInst, ordinal)
			if(result.isErr()) {
				this.uploadsSuccess = false
			} else {
				this.dispatch(dropSyncPendingDeviceToCloudOrdinal(ordinal))
			}
			this.endTask(key, result)
		}
	}
	
	createDeviceToCloudItemTask(key: SyncItemKey, ordinal: number, additionalPendingForEncounter?: boolean) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		return async () => {
			const ticker = this.startTask(key)
			const getSyncInfo = (): DeviceToCloudSyncInfo => {
				return {
					ClientId: this.clientId,
					SyncStrategy: this.syncStrategy,
					IosVersion: getIosVersion(),
					BuildNumber: getBuildNumber(),
					JsBundle: getJsBundle(),
					AdditionalPendingForEncounter: additionalPendingForEncounter,
					SyncState: this.getSyncState(),
				}
			}
			const result = await deviceToCloudSyncItem(ticker, this.axInst, ordinal, getSyncInfo)
			if(result.isErr()) {
				this.uploadsSuccess = false
			} else {
				this.dispatch(dropSyncPendingDeviceToCloudOrdinal(ordinal))
				if(result.value.value !== null) {
					// we only need to track the latest update per encounter
					// to validate that an immediate export from the database
					// does not revert the latest change
					this.deviceToCloudCodeNext.set(
						result.value.value.encounterGuid,
						result.value.value.nextCodeNext,
					)
				}
			}
			this.endTask(key, result)
		}
	}

	createImportProcessingAllowanceTask() {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.ImportProcessingAllowance,
		}
		return async () => {
			this.startTask(key)
			await sleep(1000)
			this.endTask(key, ok({
				status: SyncItemStatus.Ok,
				value: null,
			}))
		}
	}

	createEtagTestTask(etag: string) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.EtagTest,
		}
		return async () => {
			const ticker = this.startTask(key)
			const result = await cloudToDeviceSyncEtagTest(ticker, this.axInst, etag)
			this.endTask(key, result)
		}
	}
	
	createOfflineTableTask(tableId: OfflineTableId) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.OfflineTable,
			tableId,
		}
		return async () => {
			const ticker = this.startTask(key)
			if(this.writableFormIsOpen()) {
				return this.cancelTask(key)
			}
			const result = await cloudToDeviceSyncOfflineTable(ticker, this.axInst, tableId)
			this.pushDeferredWriteTask(key, result)
			this.endTask(key, result)
		}
	}

	createCloudToDeviceLdngTask(loading: boolean) {
		return async () => {
			this.dispatch(setLdng({syncCloudToDevice: loading}))
		}
	}

	createTodosTask(upn: string) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.Todo,
		}
		return async () => {
			const ticker = this.startTask(key)
			if(this.writableFormIsOpen()) {
				return this.cancelTask(key)
			}
			const result = await cloudToDeviceSyncTodos(ticker, this.axInst, upn, this.deviceToCloudCodeNext)
			this.pushDeferredWriteTask(key, result)
			this.endTask(key, result)
			if(result.isOk()) {
				for(let item of result.value.value.WorkItems) {
					this.pushTodoExportTask(item.EncounterGUID, upn)
				}
			}
			ticker(SyncItemSubtaskType.DerivativeTaskPrep)
		}
	}

	pushTodoExportTask(encounterGuid: string, upn: string) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const workItemKey: WorkItemKey = {
			type: WorkItemType.Todo,
			encounterGuid,
		}
		const expArgs: CloudToDeviceExportArgs = {
			EncounterGUID: encounterGuid,
			DeviceToCloudCodeNext: this.deviceToCloudCodeNext.get(encounterGuid),
		}
		this.runner.push(this.createExportTask(
			workItemKey,
			expArgs,
			upn,
		))
	}

	createApptsTask(upn: string) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.Appt,
		}
		return async () => {
			const ticker = this.startTask(key)
			if(this.writableFormIsOpen()) {
				return this.cancelTask(key)
			}
			const result = await cloudToDeviceSyncAppts(ticker, this.axInst, upn)
			this.pushDeferredWriteTask(key, result)
			this.endTask(key, result)
			if(result.isOk()) {
				const existingApptEncounterGuidSelector = await ApptEncounterGuidSelector.create(upn)
				for(let item of result.value.value.Appointments) {
					const workItemKey: WorkItemKey = {
						type: WorkItemType.Appointment,
						serviceApptId: item.ServiceAppointmentID,
					}
					const rPropose = proposeApptExport(item)
					if(rPropose.isErr()) {
						this.apptEncounters.push({
							serviceApptId: item.ServiceAppointmentID,
							encounterGuid: null,
							error: rPropose.error,
						})
						continue
					}
					const { memberGuid } = rPropose.value
					let expArgs: CloudToDeviceExportArgs = {
						MemberGUID: memberGuid,
						SfSvcApptId: item.ServiceAppointmentID,
						Latitude: item.Latitude,
						Longitude: item.Longitude,
						Branch_Designator: item.BranchDesignator,
					}
					// if we know this appt encounterGuid from a previous sync,
					// dan requests we use that instead of ServiceAppointmentID
					let r = existingApptEncounterGuidSelector.get(item.ServiceAppointmentID)
					if(r.isErr()) {
						this.setItemResult(
							{ type: SyncItemType.Export, workItemKey },
							err({
								status: SyncItemStatus.Unknown,
								error: serializeFusionError('createApptsTask', r.error)
							})
						)
						continue
					}
					if(r.value) {
						expArgs = {
							EncounterGUID: r.value,
						}
					}
					this.runner.push(this.createExportTask(
						workItemKey,
						expArgs,
						upn,
						item.AppointmentNumber,
					))
				}
			}
			ticker(SyncItemSubtaskType.DerivativeTaskPrep)
		}
	}
	
	createExportTask(workItemKey: WorkItemKey, args: CloudToDeviceExportArgs, upn: string, sfApptNum?: string) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.Export,
			workItemKey,
			sfApptNum,
		}
		return async () => {
			const ticker = this.startTask(key)
			if(this.writableFormIsOpen()) {
				return this.cancelTask(key)
			}
			const result = await cloudToDeviceSyncExport(ticker, this.axInst, upn, args)
			this.pushDeferredWriteTask(key, result)
			this.endTask(key, result)
			if(result.isOk()) {
				const encounter = result.value.value.Form.Encounter
				this.encounterExports.push(result.value.value)
				if(!this.branchGuids.get(encounter.BranchGUID)) {
					this.branchGuids.set(encounter.BranchGUID, true)
					this.runner.push(this.createBranchTask(encounter.BranchGUID))
				}
				let encounterRenderers: number[] = []
				if(encounter.FormRenderNbr) {
					encounterRenderers.push(encounter.FormRenderNbr)
					if(this.cfg.downloadPatchedRenderers) {
						const rUseOriginal = proposeUseOriginalRendererForDocgen(encounter.FormRenderNbr)
						if(!rUseOriginal.isOk()) {
							encounterRenderers.push(rUseOriginal.error)
						}
					}
				}
				for(let renderer of encounterRenderers) {
					if(!this.rendererNumbers.get(renderer)) {
						this.rendererNumbers.set(renderer, true)
						this.runner.push(this.createRendererTask(renderer))
					}
				}
				if(workItemKey.type === WorkItemType.Appointment) {
					this.apptEncounters.push({
						serviceApptId: workItemKey.serviceApptId,
						encounterGuid: encounter.Encounter_GUID,
						error: null,
					})
				}
			}
			ticker(SyncItemSubtaskType.DerivativeTaskPrep)
		}
	}

	// branchInfo: also download the branch's live renderer, read from the
	// flowmap (branch preview, whose fake encounter has no renderer of its own)
	createBranchTask(branchGuid: string, branchInfo = false) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.Branch,
			branchGuid,
		}
		return async () => {
			const ticker = this.startTask(key)
			if(this.writableFormIsOpen()) {
				return this.cancelTask(key)
			}
			let result = await cloudToDeviceSyncBranch(ticker, this.axInst, branchGuid, branchInfo)
			if(result.isOk()) {
				const rFileCids = await (async (): Promise<Result<string[], FusionError>> => {
					const rDecode = safeDecode(result.value.value)
					if(rDecode.isErr()) {
						return err(serializeFusionError('cloudToDeviceSyncBranch', rDecode.error))
					}
					const rParser = FlowmapParser.create(rDecode.value)
					if(rParser.isErr()) {
						return err(serializeFusionError('cloudToDeviceSyncBranch', rParser.error))
					}
					if(branchInfo) {
						const rBranchInfo = rendererInfoFromFlowmapParser(rParser.value)
						if(rBranchInfo.isErr()) {
							return err(rBranchInfo.error)
						}
						const { rendererNumber } = rBranchInfo.value
						if(rendererNumber && !this.rendererNumbers.get(rendererNumber)) {
							this.rendererNumbers.set(rendererNumber, true)
							this.runner.push(this.createRendererTask(rendererNumber))
						}
					}
					const fileCids = Object.values(rParser.value.concepts)
						.filter(concept => concept.valueDataType === ConceptValueDataType.File)
						.map(concept => concept.cid)
					return ok(fileCids)
				})()
				ticker(SyncItemSubtaskType.Parse)
				if(rFileCids.isErr()) {
					result = err({
						status: SyncItemStatus.InvalidBody,
						error: rFileCids.error,
					})
				} else {
					this.branchFileCids.set(branchGuid, rFileCids.value)
				}
			}
			this.pushDeferredWriteTask(key, result)
			this.endTask(key, result)
		}
	}

	createRendererTask(rendererNumber: number) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.Renderer,
			rendererNumber,
		}
		return async () => {
			const ticker = this.startTask(key)
			if(this.writableFormIsOpen()) {
				return this.cancelTask(key)
			}
			const result = await cloudToDeviceSyncRenderer(ticker, this.plainAxInst, rendererNumber)
			this.pushDeferredWriteTask(key, result)
			this.endTask(key, result)
		}
	}

	createReplaceApptEncountersTask(upn: string) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.ReplaceApptEncounters,
		}
		return async () => {
			this.startTask(key)
			if(this.writableFormIsOpen()) {
				return this.cancelTask(key)
			}
			const deferredWriteTask: SyncItemDeferredWriteTask = async () => {
				for(let {serviceApptId, encounterGuid} of this.apptEncounters) {
					this.dispatch(pushLogEntry(`[${SyncRunner.name}] apptEncounter ${serviceApptId} -> ${encounterGuid}`))
				}
				const r = await replaceApptEncounters(upn, this.apptEncounters)
				if(r.isErr()) {
					return err({
						status: SyncItemStatus.Unknown,
						error: r.error,
					})
				}
				return ok()
			}
			const result: SyncItemDeferredWriteResult<null> = ok({
				status: SyncItemStatus.Ok,
				value: null,
				deferredWriteTask,
			})
			this.pushDeferredWriteTask(key, result)
			this.endTask(key, result)
		}
	}

	createEncounterFileTasks() {
		return async () => {
			for(let exp of this.encounterExports) {
				let statuses = exp.Form.ConceptStatuses
				let fileCids = this.branchFileCids.get(exp.Form.Encounter.BranchGUID) ?? []
				for(let cid of fileCids) {
					const statusValue = getConceptStatusValue(statuses[cid])
					if(_.isString(statusValue)) {
						this.runner.push(this.createEncounterFileTask(statusValue))
					}
				}
			}
		}
	}

	createEncounterFileTask(fileId: string) {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.CloudToDeviceFile,
			fileId,
		}
		return async () => {
			const ticker = this.startTask(key)
			if(this.writableFormIsOpen()) {
				return this.cancelTask(key)
			}
			const result = await cloudToDeviceSyncEncounterFile(ticker, this.axInst, this.plainAxInst, fileId)
			this.pushDeferredWriteTask(key, result)
			this.endTask(key, result)
		}
	}

	createRunDeferredWritesTask() {
		this.dispatch(incrementSyncDenominatorEstimate(1))
		const key: SyncItemKey = {
			type: SyncItemType.WriteCloudToDevice,
		}
		return async () => {
			this.startTask(key)
			const formIsOpen = this.writableFormIsOpen()
			for(let deferred of this.deferredWriteTasks) {
				if(formIsOpen) {
					this.cancelTask(deferred.key)
					continue
				}
				const r = await deferred.task()
				if(r.isErr()) {
					this.endTask(deferred.key, err(r.error))
				}
			}
			if(formIsOpen) {
				this.endTask(
					key,
					err({status: SyncItemStatus.Canceled})
				)
				return
			}
			this.endTask(
				key,
				ok({
					status: SyncItemStatus.Ok,
					value: null,
				}),
			)
		}
	}
}
